import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getConsultationPath } from "../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";
import "./AboutPage.css";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import FounderImg from "../../assets/team/Founder.jpg";

/* ════════════════════════════════════════════
   HOOKS
════════════════════════════════════════════ */

const useInView = (threshold = 0.18) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const Counter = ({ end, suffix = "", prefix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView(0.3);
  const isDecimal = !Number.isInteger(end);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const steps = duration / 16;
    const step = end / steps;
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else
        setCount(isDecimal ? parseFloat(start.toFixed(1)) : Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end, duration, isDecimal]);
  return (
    <span ref={ref}>
      {prefix}
      {isDecimal ? count.toFixed(1) : count.toLocaleString()}
      {suffix}
    </span>
  );
};

/* ════════════════════════════════════════════
   SUB-COMPONENTS
════════════════════════════════════════════ */

const PrincipleCard = ({ number, title, description, delay }) => {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`wc-about-principle-card ${inView ? "wc-about-in-view" : ""}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="wc-about-principle-num">
        {String(number).padStart(2, "0")}
      </div>
      <div className="wc-about-principle-content">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="wc-about-principle-arrow">
        <i className="fas fa-arrow-right" />
      </div>
    </div>
  );
};

const PillarCard = ({ icon, label, color, delay, inView }) => (
  <div
    className={`wc-pillar-card ${inView ? "wc-about-in-view" : ""}`}
    style={{ transitionDelay: `${delay}ms` }}
  >
    <div
      className="wc-pillar-icon"
      style={{
        background: `${color}18`,
        color,
        border: `1.5px solid ${color}28`,
      }}
    >
      <i className={`fas ${icon}`} />
    </div>
    <span className="wc-pillar-label">{label}</span>
    <i className="fas fa-circle-check wc-pillar-check" style={{ color }} />
  </div>
);

/* ── Founder Card ── */
const FounderCard = ({ inView }) => (
  <div className={`wc-founder-card ${inView ? "wc-about-in-view" : ""}`}>
    {/* Photo placeholder */}
    <div className="wc-founder-photo-wrap">
      <div className="wc-founder-photo">
        <img
          src={FounderImg}
          alt="Abhinav Bakiwala – Founder & Director"
          className="wc-founder-photo-img"
        />
      </div>
      <div className="wc-founder-photo-ring" />
      <div className="wc-founder-photo-badge">
        <i className="fas fa-crown" /> Founder
      </div>
    </div>

    {/* Info */}
    <div className="wc-founder-info">
      <div className="wc-founder-name-row">
        <h3 className="wc-founder-name">Abhinav Bakiwala</h3>
        <span className="wc-founder-verified">
          <i className="fas fa-circle-check" />
        </span>
      </div>
      <p className="wc-founder-role">
        Founder &amp; Director – Wealth Compounders
      </p>

      <p className="wc-founder-bio">
        With a strong vision for ethical wealth creation and client-centric
        advisory, Abhinav Bakiwala founded Wealth Compounders to provide
        disciplined, transparent, and long-term financial solutions. His
        leadership focuses on building trust-based relationships and delivering
        consistent value to clients.
      </p>

      {/* Quick stats */}
      <div className="wc-founder-stats">
        <div className="wc-founder-stat">
          <span className="wc-founder-stat-val">20+</span>
          <span className="wc-founder-stat-lbl">Years Experience</span>
        </div>
        <div className="wc-founder-stat-div" />
        <div className="wc-founder-stat">
          <span className="wc-founder-stat-val">800+</span>
          <span className="wc-founder-stat-lbl">Clients Served</span>
        </div>
      </div>

      {/* Awards */}
      <div className="wc-founder-section-label">
        <i className="fas fa-trophy" /> Awards &amp; Recognition
      </div>
      <div className="wc-founder-awards">
        <span className="wc-founder-award">
          <i className="fas fa-medal" /> BNI Diamond Award
        </span>
      </div>

      {/* Affiliations */}
      <div className="wc-founder-section-label" style={{ marginTop: 16 }}>
        <i className="fas fa-handshake" /> Business Affiliations
      </div>
      <div className="wc-founder-affiliations">
        <span className="wc-founder-affil">
          <i className="fas fa-building" /> Authorized NJ Distributor
        </span>
        {/* <span className="wc-founder-affil">
          <i className="fas fa-building" /> Authorized Motilal Oswal Distributor
        </span> */}
      </div>
    </div>
  </div>
);

/* ── Team Member Card ── */
const TeamCard = ({ name, role, exp, qualification, delay, inView }) => (
  <div
    className={`wc-team-card ${inView ? "wc-about-in-view" : ""}`}
    style={{ transitionDelay: `${delay}ms` }}
  >
    {/* Photo placeholder */}
    <div className="wc-team-photo">
      <div className="wc-team-photo-inner">
        <i className="fas fa-user wc-team-photo-icon" />
      </div>
      <div className="wc-team-photo-shimmer" />
    </div>

    <div className="wc-team-info">
      <h4 className="wc-team-name">{name}</h4>
      <p className="wc-team-role">{role}</p>

      <div className="wc-team-meta">
        <span className="wc-team-exp">
          <i className="fas fa-briefcase" /> {exp}
        </span>
        {qualification && (
          <span className="wc-team-qual">
            <i className="fas fa-graduation-cap" /> {qualification}
          </span>
        )}
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════════
   PAGE
════════════════════════════════════════════ */
const AboutPage = () => {
  const navigate = useNavigate();

  const [heroRef, heroInView] = useInView(0.1);
  const [compoundRef, compoundInView] = useInView(0.12);
  const [philoRef, philoInView] = useInView(0.1);
  const [founderRef, founderInView] = useInView(0.1);
  const [teamRef, teamInView] = useInView(0.1);

  const stats = [
    { value: 800, suffix: "+", prefix: "", label: "Investors Served" },
    { value: 20, suffix: "+", prefix: "", label: "Years of Excellence" },
    { value: 99, suffix: "%", prefix: "", label: "Client Satisfaction" },
    { value: 100, suffix: "cr+", prefix: "₹", label: "Assets Managed" },
  ];

  const pillars = [
    {
      icon: "fa-hourglass-start",
      label: "Long-term Discipline",
      color: "#2563eb",
    },
    { icon: "fa-gem", label: "High-Quality Assets", color: "#7c3aed" },
    {
      icon: "fa-layer-group",
      label: "Diversified Portfolios",
      color: "#0891b2",
    },
    { icon: "fa-chart-line", label: "Regular Monitoring", color: "#10b981" },
  ];

  const growthRows = [
    { year: "Year 1", amount: "₹1L", pct: "15%" },
    { year: "Year 5", amount: "₹1.6L", pct: "30%" },
    { year: "Year 10", amount: "₹2.6L", pct: "50%" },
    { year: "Year 20", amount: "₹6.7L", pct: "73%" },
    { year: "Year 30", amount: "₹17L", pct: "100%" },
  ];

  const principles = [
    {
      title: "Long-Term Investing",
      description:
        "Patience is a competitive advantage. Wealth is built over decades, not days — and we never lose sight of that.",
    },
    {
      title: "Quality over Speculation",
      description:
        "We select fundamentally strong assets with proven track records, not momentum trades or speculative bets.",
    },
    {
      title: "Diversification for Risk Mgmt.",
      description:
        "Spreading capital intelligently across asset classes reduces volatility and protects your portfolio on the downside.",
    },
    {
      title: "Consistent Monitoring",
      description:
        "Markets evolve. We review and rebalance portfolios regularly so you're always aligned with your financial goals.",
    },
    {
      title: "Goal-Based Wealth Creation",
      description:
        "Every strategy begins with your milestones — retirement, education, legacy — and works backward from there.",
    },
  ];

  const team = [
    {
      name: "Dhaval Jain",
      role: "Chief Operating Officer",
      exp: "5+ Years",
      qualification: "MBA Finance & Marketing",
    },
    {
      name: "Shobhit Kasliwal",
      role: "Head of Operations",
      exp: "13+ Years",
      qualification: null,
    },
    {
      name: "Pawan Saini",
      role: "Head of NJ Operations",
      exp: "15+ Years",
      qualification: null,
    },
    {
      name: "Dashrath Sahu",
      role: "Relationship Manager",
      exp: "10+ Years",
      qualification: null,
    },
    {
      name: "Vivek Sharma",
      role: "Relationship Manager",
      exp: "10+ Years",
      qualification: null,
    },
    {
      name: "Lokesh Kumawat",
      role: "Relationship Manager",
      exp: "5+ Years",
      qualification: null,
    },
  ];

  return (
    <div className="wc-about-wrapper">
      <Navbar />

      {/* ══ SECTION 1 — HERO ══ */}
      <section className="wc-about-hero" id="wc-about-hero" ref={heroRef}>
        <div className="wc-about-blob wc-blob-1" />
        <div className="wc-about-blob wc-blob-2" />
        <div className="wc-about-dotgrid" />

        <div className="wc-about-container">
          <div
            className={`wc-about-hero-text ${heroInView ? "wc-about-in-view" : ""}`}
          >
            <div className="wc-about-eyebrow">
              <span className="wc-about-eyebrow-dot" />
              Compounding Your Wealth, Securing Your Future
            </div>

            <h1 className="wc-about-hero-h1">
              🔹 Our Mission
            </h1>

            <p className="wc-about-hero-body">
At Wealth Compounders, our mission is to help people grow their wealth consistently through the power of compounding, disciplined investing, and strategic financial planning.

            </p>
            
            <h1 className="wc-about-hero-h1">
              🔹 <em className="wc-about-em"> Our Vision</em>
            </h1>
            <p className="wc-about-hero-body">

Our vision is to create a trusted financial ecosystem where every investor — whether beginner or experienced — can build long-term wealth using research-based strategies, modern tools, and expert guidance.

            </p>

            {/* Stats Column */}
            {/* <div className="wc-about-stats-strip">
              {stats.map((s) => (
                <div className="wc-about-stat" key={s.label}>
                  <span className="wc-about-stat-val">
                    <Counter
                      end={s.value}
                      suffix={s.suffix}
                      prefix={s.prefix}
                    />
                  </span>
                  <span className="wc-about-stat-lbl">{s.label}</span>
                </div>
              ))}
            </div> */}

            {/* <div className="wc-about-ctas">
              <button
                className="wc-about-btn-primary"
                onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.ABOUT))}
              >
                Free Consultation <i className="fas fa-arrow-right" />
              </button>
              <button
                className="wc-about-btn-ghost"
                onClick={() => navigate(-1)}
              >
                <i className="fas fa-arrow-left" /> Back
              </button>
            </div> */}
          </div>

          <div
            className={`wc-about-hero-visual ${heroInView ? "wc-about-in-view wc-about-visual-delay" : ""}`}
          >
            <div className="wc-vis-card wc-vis-main">
              <div className="wc-vis-header">
                <div className="wc-vis-dots">
                  <span />
                  <span />
                  <span />
                </div>
                <span className="wc-vis-title">Portfolio Overview</span>
                <span className="wc-vis-live">
                  <span className="wc-vis-live-dot" /> Live
                </span>
              </div>
              <div className="wc-vis-bars">
                {[38, 52, 44, 68, 58, 78, 72, 94].map((h, i) => (
                  <div key={i} className="wc-vis-bar-col">
                    <div
                      className="wc-vis-bar"
                      style={{
                        height: `${h}%`,
                        animationDelay: `${i * 0.07 + 0.4}s`,
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="wc-vis-footer">
                <span className="wc-vis-growth">
                  <i className="fas fa-arrow-trend-up" /> +57% Growth
                </span>
                <span className="wc-vis-period">12 months</span>
              </div>
            </div>
            {[
              { icon: "fa-users", text: "Built for Wealth Creations", cls: "wc-vis-badge-2" },
              {
                icon: "fa-star",
                text: "Smart Investing Simplified",
                cls: "wc-vis-badge-3",
              },
              {
                icon: "fa-shield-halved",
                text: "Structured Approach",
                cls: "wc-vis-badge-1",
              },
            ].map((b) => (
              <div key={b.cls} className={`wc-vis-badge ${b.cls}`}>
                <i className={`fas ${b.icon}`} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

     

      {/* ══ SECTION 3 — FOUNDER ══ */}
      <section
        className="wc-about-founder"
        id="wc-about-founder"
        ref={founderRef}
      >
        <div className="wc-about-founder-glow" />
        <div className="wc-about-container">
          <div
            className="wc-about-pill wc-about-pill-blue"
            style={{ marginBottom: 12 }}
          >
            <i className="fas fa-star" /> Leadership
          </div>
          <div className="wc-about-founder-header">
            <h2 className="wc-about-section-h2">
              Meet Our <em className="wc-about-em">Founder</em>
            </h2>
            <p className="wc-about-section-body" style={{ maxWidth: 520 }}>
              The vision, values, and drive behind Wealth Compounders — built on
              two decades of trust, discipline, and unwavering commitment to
              client success.
            </p>
          </div>
          <FounderCard inView={founderInView} />
        </div>
      </section>

      {/* ══ SECTION 4 — TEAM ══ */}
      <section className="wc-about-team" id="wc-about-team" ref={teamRef}>
        <div className="wc-about-team-bg" />
        <div className="wc-about-container">
          <div className="wc-about-pill wc-about-pill-light">
            <i className="fas fa-users" /> Our People
          </div>
          <div className="wc-about-team-header">
            <h2 className="wc-about-section-h2 wc-h2-light">
              Meet Our <em className="wc-about-em-light">Team</em>
            </h2>
            <p
              className="wc-about-section-body wc-body-light"
              style={{ maxWidth: 520 }}
            >
              A team of seasoned professionals, each bringing deep expertise and
              a shared passion for delivering real financial results to every
              client.
            </p>
          </div>

          <div className="wc-team-grid">
            {team.map((member, i) => (
              <TeamCard
                key={member.name}
                {...member}
                delay={i * 80 + 100}
                inView={teamInView}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ══ SECTION 5 — PHILOSOPHY ══ */}
      <section
        className="wc-about-philo"
        id="wc-about-philosophy"
        ref={philoRef}
      >
        <div className="wc-about-philo-bg" />
        <div className="wc-about-container wc-about-philo-inner">
          <div className="wc-about-pill wc-about-pill-light">
            <i className="fas fa-compass" /> Our Principles
          </div>
          <div className="wc-about-philo-header">
            <h2 className="wc-about-section-h2 wc-h2-light">
              Our Investment
              <br />
              <em className="wc-about-em-light">Philosophy</em>
            </h2>
            <p className="wc-about-section-body wc-body-light">
              At Wealth Compounders, our investment philosophy is built on five
              core principles. We focus on building portfolios that grow
              steadily rather than chasing short-term market trends.
            </p>
          </div>
          <div className="wc-about-principles">
            {principles.map((p, i) => (
              <PrincipleCard
                key={p.title}
                number={i + 1}
                {...p}
                delay={i * 80}
              />
            ))}
          </div>
          <div
            className={`wc-about-philo-cta ${philoInView ? "wc-about-in-view" : ""}`}
          >
            <div className="wc-philo-cta-left">
              <h3>Ready to start building wealth?</h3>
              <p>
                Join 800+ investors who trust Wealth Compounders with their
                financial future.
              </p>
            </div>
            <button
              className="wc-about-btn-cta-white"
              onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.ABOUT))}
            >
              Book Free Consultation <i className="fas fa-arrow-right" />
            </button>
          </div>
        </div>
      </section>

       {/* ══ SECTION 2 — COMPOUNDING ══ */}
      <section
        className="wc-about-compound"
        id="wc-about-compound"
        ref={compoundRef}
      >
        <div className="wc-about-compound-glow" />
        <div className="wc-about-container">
          <div className="wc-about-pill wc-about-pill-blue">
            <i className="fas fa-infinity" /> The Science of Growth
          </div>
          <div
            className={`wc-about-compound-grid ${compoundInView ? "wc-about-in-view" : ""}`}
          >
            <div className="wc-about-compound-left">
              <h2 className="wc-about-section-h2">
                The Power of
                <br />
                <em className="wc-about-em">Compounding</em>
              </h2>
              <p className="wc-about-section-body">
                Compounding is one of the most powerful forces in investing. It
                allows your investments to generate earnings, and those earnings
                generate even more returns over time. At Wealth Compounders, we
                focus on strategies that let your money grow steadily and
                relentlessly.
              </p>
              <p className="wc-about-section-body">
                The earlier you start investing, the greater the impact
                compounding can have on your financial future. Time is the most
                valuable asset you have — and we help you use it wisely.
              </p>
              <div className="wc-compound-chart">
                {growthRows.map((row, i) => (
                  <div className="wc-compound-row" key={i}>
                    <span className="wc-compound-year">{row.year}</span>
                    <div className="wc-compound-track">
                      <div
                        className="wc-compound-fill"
                        style={{
                          width: compoundInView ? row.pct : "0%",
                          transitionDelay: `${i * 0.15 + 0.2}s`,
                        }}
                      />
                    </div>
                    <span className="wc-compound-amt">{row.amount}</span>
                  </div>
                ))}
                <p className="wc-compound-footnote">
                  <i className="fas fa-circle-info" />
                  Illustrative: ₹1L invested at 10% p.a. compounded annually
                </p>
              </div>
            </div>

            <div className="wc-about-pillars">
              <p className="wc-pillars-eyebrow">We help you grow through:</p>
              {pillars.map((p, i) => (
                <PillarCard
                  key={p.label}
                  {...p}
                  delay={i * 90 + 100}
                  inView={compoundInView}
                />
              ))}
              <div
                className={`wc-compound-quote ${compoundInView ? "wc-about-in-view" : ""}`}
                style={{ transitionDelay: "560ms" }}
              >
                <i className="fas fa-quote-left wc-quote-icon" />
                <p>
                  "Compound interest is the eighth wonder of the world. He who
                  understands it, earns it; he who doesn't, pays it."
                </p>
                <span>— Albert Einstein</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
